import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Thin wrappers over the control-plane callables.
 *
 * The admin portal deliberately does NOT read Firestore directly. Every action
 * goes through a callable that re-checks `platformAdmin` server-side, so
 * authorisation never depends on the client behaving — and every mutation has
 * one place to record an audit entry.
 */
function call<TIn extends object, TOut>(name: string) {
  return async (data: TIn): Promise<TOut> => (await httpsCallable<TIn, TOut>(functions, name)(data)).data;
}

export type OrgStatus = 'active' | 'suspended';
export type Role = 'owner' | 'admin' | 'manager' | 'tech' | 'viewer';

/** Where somebody actually stands, as opposed to what they are allowed to do. */
export type MemberState = 'active' | 'invited' | 'disabled';

/**
 * What somebody may do to the BUSINESS, as opposed to inside one tenant.
 * Owner hands out access; Admin runs the platform but cannot; Employee looks.
 */
export type PlatformRole = 'owner' | 'admin' | 'employee';
export type PlatformCapability =
  | 'tenants.view' | 'tenants.manage' | 'plans.manage'
  | 'integrations.manage' | 'features.manage' | 'audit.view' | 'admins.manage';
export type Source = 'datto' | 'sp' | 'sc' | 'msp' | 'unitrends' | 'email';

export interface Counts {
  total: number; healthy: number; warning: number; failed: number; paused: number; unknown: number;
}
export const ZERO: Counts = { total: 0, healthy: 0, warning: 0, failed: 0, paused: 0, unknown: 0 };

export interface OrgUsage {
  orgId: string;
  name: string;
  slug: string;
  status: OrgStatus;
  planId: string | null;
  createdAt: string | null;
  counts: Counts;
  bySource: Record<Source, Counts>;
  memberCount: number;
  lastSyncedAt: string | null;
  lastSyncOk: Record<Source, boolean> | null;
}

/**
 * A membership joined against the Auth record.
 *
 * The role says what somebody may do; it cannot say whether they ever turned
 * up. `state` and `mfaEnrolled` come from Firebase Auth and answer the two
 * questions the console previously could not: has this invitation been
 * accepted, and is this account protected by a second factor.
 */
export interface Member {
  uid: string;
  email: string | null;
  role: Role;
  state: MemberState;
  disabled: boolean;
  lastSignIn: string | null;
  emailVerified: boolean;
  mfaEnrolled: boolean;
}

export interface SyncRun {
  orgId: string; orgName: string; source: string; ok: boolean;
  error: string | null; changed: number; durationMs: number | null; startedAt: string | null;
}

export interface PlatformUser {
  uid: string; email: string | null; platformAdmin: boolean;
  memberships: { orgId: string; orgName: string; role: string }[];
  /** Owner, Admin or Employee. Absent for people who are not platform admins. */
  platformRole?: PlatformRole;
  /** From Auth, same join as Member. A platform admin without MFA matters most. */
  state: MemberState;
  disabled: boolean;
  lastSignIn: string | null;
  mfaEnrolled: boolean;
}

export interface Plan {
  id: string; name: string;
  maxTargets: number | null; maxOrgUsers: number | null;
  features: string[]; active: boolean;
  /**
   * Features REMOVED from this plan. An opt-out list, not an allow-list -
   * absent or empty means the plan includes everything, so a plan written
   * before this existed keeps working. See functions/src/plan-features.ts.
   */
  disabledFeatures?: string[];
}

export interface OptionalFeature { id: string; label: string; effect: string }

export interface AuditEntry {
  id: string; action: string; actorEmail: string | null;
  detail: Record<string, unknown>; at: string | null;
}

export const overview = call<Record<string, never>, {
  orgs: OrgUsage[]; totals: Counts;
  orgCount: number; activeOrgCount: number; suspendedOrgCount: number;
}>('adminOverview');

export const syncHealth = call<Record<string, never>, { runs: SyncRun[] }>('adminSyncHealth');
export const listUsers = call<Record<string, never>, { users: PlatformUser[] }>('adminListUsers');
export const listAudit = call<{ limit?: number }, { entries: AuditEntry[] }>('adminListAudit');

export const createOrg = call<{ name: string; slug: string }, { ok: boolean; orgId: string }>('adminCreateOrg');
export const setOrgStatus = call<{ orgId: string; status: OrgStatus }, { ok: boolean }>('adminSetOrgStatus');
export const setOrgPlan = call<{ orgId: string; planId: string | null }, { ok: boolean }>('adminSetOrgPlan');
export const setOrgFeatures =
  call<{ orgId: string; disabledFeatures: string[] }, { ok: boolean; disabledFeatures: string[] }>('adminSetOrgFeatures');
export const setPlatformAdmin = call<{ email: string; grant: boolean }, { ok: boolean }>('adminSetPlatformAdmin');

/**
 * Add a platform admin, creating the account if the address has none.
 * setPlatformAdmin deliberately refuses an unknown address; this is the
 * explicit "yes, make one" version, and it emails a one-time sign-in link.
 */
export const setPlatformRole =
  call<{ uid: string; role: PlatformRole }, { ok: boolean; role: PlatformRole }>('adminSetPlatformRole');

export interface PlatformRoleCatalog {
  myRole: PlatformRole;
  myCapabilities: PlatformCapability[];
  capabilities: PlatformCapability[];
  assignable: PlatformRole[];
  roles: { role: PlatformRole; label: string; description: string; capabilities: PlatformCapability[] }[];
}

/**
 * The catalogue plus what THIS caller may do.
 *
 * The console asks rather than deciding for itself, so a hidden control and a
 * refused call can never disagree - the server is the only thing that knows.
 */
export const platformRoleCatalog =
  call<Record<string, never>, PlatformRoleCatalog>('adminPlatformRoleCatalog');

export const invitePlatformAdmin = call<{ email: string; role?: PlatformRole }, {
  ok: boolean; uid: string; created: boolean; emailed: boolean; emailError: string | null;
}>('adminInvitePlatformAdmin');

/** Disables the FIREBASE USER - locks them out of the console and every tenant. */
export const setUserDisabled =
  call<{ uid: string; disabled: boolean }, { ok: boolean; disabled: boolean }>('adminSetUserDisabled');
export const findUser = call<{ email: string }, {
  found: boolean; uid?: string; email?: string | null;
  disabled?: boolean; lastSignIn?: string | null; createdAt?: string | null;
}>('adminFindUser');

export const orgTeam = call<{ orgId: string }, { members: Member[] }>('orgTeam');
export const setMemberRole = call<{ orgId: string; email: string; role: Role }, { ok: boolean }>('setMemberRole');
export const removeMember = call<{ orgId: string; uid: string }, { ok: boolean }>('removeMember');
export const setMemberDisabled =
  call<{ orgId: string; uid: string; disabled: boolean }, { ok: boolean; disabled: boolean }>('setMemberDisabled');
export const resendInvite =
  call<{ orgId: string; uid: string }, { ok: boolean; sentTo: string }>('resendInvite');

// ---------------------------------------------------------------------------
// Registry vendors - the unproven integrations.
//
// Read-only from the console's point of view: it can show what a tenant has
// configured and how far it has been proven, but verification is a decision
// made against a dry run on the tenant side, not something to click through
// from here.
// ---------------------------------------------------------------------------

export interface VendorSummary {
  id: string;
  label: string;
  capabilities: ('collect' | 'tickets' | 'history' | 'knowledge')[];
  confidence: 'verified' | 'documented' | 'inferred';
  docs: string;
  notes: string | null;
  configured: boolean;
  enabled: boolean;
  verified: boolean;
  verifiedBy: string | null;
}

export const listVendors = call<{ orgId: string }, { vendors: VendorSummary[] }>('listVendors');

export const listPlans =
  call<Record<string, never>, { plans: Plan[]; optionalFeatures: OptionalFeature[] }>('adminListPlans');
export const upsertPlan = call<Partial<Plan> & { id: string }, { ok: boolean }>('adminUpsertPlan');
export const deletePlan = call<{ id: string }, { ok: boolean }>('adminDeletePlan');

export const refreshMyClaims = call<Record<string, never>, { claims: unknown }>('refreshMyClaims');

/** Callable errors arrive wrapped; surface the useful part. */
export function errText(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) return String((err as Error).message);
  return String(err);
}

export const SOURCE_LABEL: Record<Source, string> = {
  datto: 'Datto BCDR',
  sp: 'ShadowProtect',
  sc: 'SC Image Manager',
  msp: 'MSP360 Backup',
  email: 'Emailed reports',
  unitrends: 'Kaseya Backup Portal',
};

export function ago(iso: string | null): string {
  if (!iso) return 'never';
  const mins = (Date.now() - new Date(iso).getTime()) / 60000;
  if (mins < 2) return 'just now';
  if (mins < 60) return `${Math.floor(mins)}m ago`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ago`;
  return `${Math.floor(mins / 1440)}d ago`;
}

// ---------------------------------------------------------------------------
// Feature requests
// ---------------------------------------------------------------------------

export type FeatureStatus = 'new' | 'planned' | 'in-progress' | 'shipped' | 'declined';

export interface FeatureRequest {
  id: string;
  title: string;
  details: string;
  status: FeatureStatus;
  voteCount: number;
  createdAt: number;
  uid: string;
  email: string;
  name: string;
  orgId: string;
  orgName: string;
  appVersion: string;
  view: string;
  adminNote: string;
}

export const listFeatureRequests = call<
  Record<string, never>,
  { requests: FeatureRequest[]; counts: Record<FeatureStatus, number>; fetchedAt: number }
>('adminListFeatureRequests');

/**
 * `newCount` is "arrived since you last looked" (the badge); `untriaged` is
 * status === 'new' (the work queue). They are different numbers on purpose —
 * see features.tsx.
 */
export const featureRequestCount = call<
  Record<string, never>,
  { newCount: number; untriaged: number; seenAt: number }
>('adminFeatureRequestCount');

export const markFeatureRequestsSeen =
  call<{ at: number }, { ok: boolean; seenAt: number }>('adminMarkFeatureRequestsSeen');

export const updateFeatureRequest =
  call<{ id: string; status?: FeatureStatus; adminNote?: string }, { ok: boolean }>('adminUpdateFeatureRequest');

export const deleteFeatureRequest =
  call<{ id: string }, { ok: boolean }>('adminDeleteFeatureRequest');

// ---------------------------------------------------------------------------
// Platform integrations — the product's own backend credentials.
// Never confuse these with a tenant's vendor credentials: see
// functions/src/assistant.ts for why they are stored separately.
// ---------------------------------------------------------------------------

export interface PlatformIntegration {
  provider: string;
  label: string;
  hint: string;
  fields: {
    key: string;
    label: string;
    state: 'set' | 'unset' | 'unreadable';
    /** false = ordinary config, shown and editable in the clear. */
    secret?: boolean;
    /** Present only for non-secret fields. Credentials never come back. */
    value?: string | null;
    placeholder?: string;
  }[];
  enabled: boolean;
  updatedAt: number | null;
  updatedBy: string | null;
}

export const getPlatformIntegrations = call<
  Record<string, never>,
  { integrations: PlatformIntegration[] }
>('adminGetPlatformIntegrations');

export const setPlatformIntegration = call<
  { provider: string; values: Record<string, string>; enabled?: boolean },
  { ok: boolean; fieldsWritten: string[]; warning?: string | null }
>('adminSetPlatformIntegration');

export const testPlatformIntegration = call<
  { provider: string },
  { ok: boolean; message: string }
>('adminTestPlatformIntegration');
